/**
 * Intent metadata that drives the toolbar UI (labels, icons, when each applies).
 * Icon names are lucide-react component names — the consumer maps name → icon so
 * this stays free of any icon-library import.
 */

import type { AiReplyIntent } from "./types";

export interface AiReplyIntentMeta {
  intent: AiReplyIntent;
  /** Button / action label. */
  label: string;
  /** lucide-react icon name (e.g. "Sparkles"). */
  icon: string;
  /** Requires existing draft text to act on. */
  requiresBody: boolean;
  /** Driven by a freeform instruction the user types. */
  usesInstruction: boolean;
  /** Short helper text. */
  description: string;
}

export const AI_REPLY_INTENTS: Record<AiReplyIntent, AiReplyIntentMeta> = {
  draft: {
    intent: "draft",
    label: "Help me write",
    icon: "Sparkles",
    requiresBody: false,
    usesInstruction: true,
    description: "Describe the message and let AI write a first draft.",
  },
  custom: {
    intent: "custom",
    label: "Describe a change",
    icon: "Wand2",
    requiresBody: true,
    usesInstruction: true,
    description: "Tell AI how to change your draft.",
  },
  polish: {
    intent: "polish",
    label: "Polish",
    icon: "Wand2",
    requiresBody: true,
    usesInstruction: false,
    description: "Fix typos, grammar, and formatting without changing meaning.",
  },
  formal: {
    intent: "formal",
    label: "Formal",
    icon: "Briefcase",
    requiresBody: true,
    usesInstruction: false,
    description: "Rewrite as a polished, formal business email.",
  },
  friendly: {
    intent: "friendly",
    label: "Friendly",
    icon: "Smile",
    requiresBody: true,
    usesInstruction: false,
    description: "Rewrite in a warmer, friendlier tone.",
  },
  shorten: {
    intent: "shorten",
    label: "Shorten",
    icon: "Scissors",
    requiresBody: true,
    usesInstruction: false,
    description: "Condense while keeping the main points.",
  },
};

/** The four one-click transform buttons shown when a draft already exists. */
export const AI_REPLY_TRANSFORM_INTENTS: readonly AiReplyIntent[] = [
  "polish",
  "formal",
  "friendly",
  "shorten",
];
